import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import {
  ArrowRight,
  Banknote,
  Building2,
  ChevronDown,
  ChevronRight,
  Package,
  Search,
  Truck,
  Undo2,
  Users,
  type LucideIcon,
} from "lucide-react";
import "./new-entry-chooser.css";

export type NewEntrySectionId = "delivery" | "out" | "in";
export type NewEntryGroupId = "delivery" | "staff" | "suppliers" | "spending" | "office" | "returned";

export interface NewEntryOption {
  id: string;
  nameEn: string;
  nameAr: string;
  descriptionEn: string;
  descriptionAr: string;
  sectionId: NewEntrySectionId;
  groupId: NewEntryGroupId | string;
  disabled?: boolean;
  onSelect: () => void;
  effectEn?: string;
  effectAr?: string;
}

export interface NewEntryChooserProps {
  branchName: string;
  workspaceId: string;
  userId: string;
  arabic: boolean;
  close?: () => void;
  options: NewEntryOption[];
  deliveryLoading?: boolean;
  deliveryError?: boolean | string;
  onRetryDelivery?: () => void;
  openingBalanceOption?: NewEntryOption;
}

type GroupDefinition = {
  id: NewEntryGroupId;
  nameEn: string;
  nameAr: string;
  icon: LucideIcon;
};

const GROUPS: GroupDefinition[] = [
  { id: "delivery", nameEn: "Delivery company", nameAr: "شركة التوصيل", icon: Truck },
  { id: "staff", nameEn: "Staff", nameAr: "الموظفين", icon: Users },
  { id: "suppliers", nameEn: "Suppliers and jobs", nameAr: "الموردين والشغلانات", icon: Package },
  { id: "spending", nameEn: "Spending and handovers", nameAr: "مصروفات وتوريد", icon: Banknote },
  { id: "office", nameEn: "From the office and sales", nameAr: "من الإدارة والمبيعات", icon: Building2 },
  { id: "returned", nameEn: "Paid back and other", nameAr: "مرتجعات وغيرها", icon: Undo2 },
];

const SECTION_COPY: Record<NewEntrySectionId, { en: string; ar: string; color: string }> = {
  delivery: { en: "Delivery company", ar: "شركة التوصيل", color: "blue" },
  out: { en: "Money out", ar: "أموال خارجة", color: "red" },
  in: { en: "Money in", ar: "أموال داخلة", color: "green" },
};

const SEARCH_KEYWORDS: Record<string, string> = {
  handover: "handover collector collection handover hq توريد للإدارة للاداره محصل تحصيل تسليم",
  staff_pay: "salary wage مرتب مرتبات قبض يومية",
  expense: "gas petrol transport cleaning بنزين مواصلات نضافة كهربا انترنت مية",
  take: "market سوق float عهدة",
  job: "maintenance repair صيانة فني",
  supplier: "vendor invoice فاتورة مورد",
  advance: "loan سلف",
  advance_repayment: "loan سلفة سداد",
  till: "sales z report مبيعات درج",
  refund: "return مرتجع",
  topup: "float عهدة hq",
  delivery_pay: "talabat instashop rider طلبات خصم تحصيل",
  delivery_charge: "talabat instashop rider طلبات خصم تحصيل",
  delivery_received: "talabat instashop rider طلبات خصم تحصيل",
};

const DEFAULT_OPEN: NewEntryGroupId[] = ["delivery", "spending", "office"];
const GROUP_BY_ID = new Map(GROUPS.map(group => [group.id, group]));

function normalizeArabic(value: string): string {
  return value
    .toLocaleLowerCase()
    .replace(/[إأآٱ]/g, "ا")
    .replace(/ة/g, "ه")
    .replace(/ى/g, "ي")
    .replace(/[\u064B-\u065F\u0670\u06D6-\u06ED]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function searchIdentity(workspaceId: string, userId: string): string {
  return JSON.stringify([workspaceId, userId]);
}

function persistenceKey(identity: string): string {
  return `branch-cash:new-entry-groups:${identity}`;
}

function getStoredGroups(identity: string): NewEntryGroupId[] {
  try {
    const raw = window.localStorage.getItem(persistenceKey(identity));
    if (!raw) return DEFAULT_OPEN;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return DEFAULT_OPEN;
    const value = parsed as { version?: unknown; identity?: unknown; openGroupIds?: unknown };
    if (
      value.version !== 1 ||
      !Array.isArray(value.identity) ||
      JSON.stringify(value.identity) !== identity ||
      !Array.isArray(value.openGroupIds)
    ) return DEFAULT_OPEN;
    return value.openGroupIds.filter((id): id is NewEntryGroupId =>
      typeof id === "string" && GROUP_BY_ID.has(id as NewEntryGroupId),
    );
  } catch {
    return DEFAULT_OPEN;
  }
}

function optionSearchText(option: NewEntryOption, arabic: boolean): string {
  const group = GROUP_BY_ID.get(option.groupId as NewEntryGroupId);
  const section = SECTION_COPY[option.sectionId];
  return normalizeArabic([
    option.nameEn,
    option.nameAr,
    option.descriptionEn,
    option.descriptionAr,
    option.effectEn,
    option.effectAr,
    group?.nameEn,
    group?.nameAr,
    section.en,
    section.ar,
    SEARCH_KEYWORDS[option.id],
    arabic ? option.nameAr : option.nameEn,
  ].filter(Boolean).join(" "));
}

function HighlightedText({ text, query }: { text: string; query: string }) {
  const tokens = normalizeArabic(query).split(" ").filter(Boolean);
  if (!tokens.length || !text) return <>{text}</>;
  const normalized = normalizeArabic(text);
  const ranges: Array<[number, number]> = [];
  for (const token of tokens) {
    const at = normalized.indexOf(token);
    if (at >= 0) ranges.push([at, at + token.length]);
  }
  if (!ranges.length) return <>{text}</>;
  // Arabic normalization can change character positions; in that case mark the
  // whole source label instead of highlighting the wrong glyphs.
  if (normalized.length !== text.toLocaleLowerCase().replace(/\s+/g, " ").trim().length) {
    return <mark>{text}</mark>;
  }
  ranges.sort((a, b) => a[0] - b[0]);
  const merged = ranges.reduce<Array<[number, number]>>((result, range) => {
    const previous = result[result.length - 1];
    if (previous && range[0] <= previous[1]) previous[1] = Math.max(previous[1], range[1]);
    else result.push([...range]);
    return result;
  }, []);
  const pieces: ReactNode[] = [];
  let cursor = 0;
  merged.forEach(([start, end], index) => {
    if (start > cursor) pieces.push(text.slice(cursor, start));
    pieces.push(<mark key={`hit-${index}`}>{text.slice(start, end)}</mark>);
    cursor = end;
  });
  if (cursor < text.length) pieces.push(text.slice(cursor));
  return <>{pieces}</>;
}

function groupName(groupId: string, arabic: boolean): string {
  const group = GROUP_BY_ID.get(groupId as NewEntryGroupId);
  return group ? (arabic ? group.nameAr : group.nameEn) : groupId;
}

function sectionName(sectionId: NewEntrySectionId, arabic: boolean): string {
  const section = SECTION_COPY[sectionId];
  return arabic ? section.ar : section.en;
}

export default function NewEntryChooser({
  branchName,
  workspaceId,
  userId,
  arabic,
  options,
  deliveryLoading = false,
  deliveryError = false,
  onRetryDelivery,
  openingBalanceOption,
}: NewEntryChooserProps) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const identity = searchIdentity(workspaceId, userId);
  const [expanded, setExpanded] = useState<{ identity: string; ids: NewEntryGroupId[] }>({ identity: "", ids: DEFAULT_OPEN });
  const hydratedIdentity = useRef("");
  const normalizedQuery = normalizeArabic(query);
  const searching = normalizedQuery.length > 0;

  useEffect(() => {
    const stored = getStoredGroups(identity);
    hydratedIdentity.current = identity;
    setExpanded({ identity, ids: stored });
  }, [identity]);

  useEffect(() => {
    if (expanded.identity !== identity || hydratedIdentity.current !== identity) return;
    try {
      window.localStorage.setItem(persistenceKey(identity), JSON.stringify({
        version: 1,
        identity: JSON.parse(identity) as [string, string],
        openGroupIds: expanded.ids,
      }));
    } catch {
      // Storage may be unavailable (private browsing, quota); the chooser still works.
    }
  }, [expanded, identity]);

  useEffect(() => {
    // The enclosing modal focuses its surface in its own effect.
    // Focus the search after that effect, without opening a phone keyboard.
    if (!window.matchMedia?.("(min-width: 768px)").matches) return;
    const frame=requestAnimationFrame(()=>inputRef.current?.focus());
    return ()=>cancelAnimationFrame(frame);
  }, []);

  const effectiveExpanded = expanded.identity === identity ? expanded.ids : DEFAULT_OPEN;
  const toggleGroup = useCallback((groupId: NewEntryGroupId) => {
    setExpanded(current => {
      const ids = current.identity === identity ? current.ids : DEFAULT_OPEN;
      return {
        identity,
        ids: ids.includes(groupId) ? ids.filter(id => id !== groupId) : [...ids, groupId],
      };
    });
  }, [identity]);

  const visibleOptions = useMemo(() => {
    const entries = [...options.filter(option => option.id !== "opening_balance")];
    const balanceOption = openingBalanceOption || options.find(option => option.id === "opening_balance");
    if (balanceOption && !entries.some(option => option.id === balanceOption.id)) entries.push(balanceOption);
    if (!searching) return entries;
    const words = normalizedQuery.split(" ").filter(Boolean);
    const relevance=(option:NewEntryOption)=>words.reduce((score,word)=>{
      if(normalizeArabic(`${option.nameEn} ${option.nameAr}`).includes(word))return score+4;
      if(normalizeArabic(SEARCH_KEYWORDS[option.id]||"").includes(word))return score+3;
      if(normalizeArabic(`${option.descriptionEn} ${option.descriptionAr}`).includes(word))return score+2;
      return score+1;
    },0);
    return entries.filter(option => {
      const combined = optionSearchText(option, arabic);
      return words.every(word => combined.includes(word));
    }).sort((a,b)=>relevance(b)-relevance(a));
  }, [arabic, normalizedQuery, openingBalanceOption, options, searching]);

  const enabledSearchResults = visibleOptions.filter(option => !option.disabled);
  const firstEnabledId = searching ? enabledSearchResults[0]?.id : undefined;

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape" && query) {
      event.preventDefault();
      event.stopPropagation();
      setQuery("");
    } else if (event.key === "Enter" && searching && firstEnabledId) {
      event.preventDefault();
      visibleOptions.find(option => option.id === firstEnabledId)?.onSelect();
    }
  };

  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      if (!window.matchMedia?.("(min-width: 768px)").matches) return;
      const target = event.target;
      if (target instanceof HTMLElement && (
        target.isContentEditable ||
        target.matches("input, textarea, select, [role='textbox']")
      )) return;
      event.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const renderOption = (option: NewEntryOption, index: number, isSearchResult = false) => {
    const title = arabic ? option.nameAr || option.nameEn : option.nameEn || option.nameAr;
    const description = arabic ? option.descriptionAr || option.descriptionEn : option.descriptionEn || option.descriptionAr;
    const displayed=normalizeArabic(`${title} ${description} ${groupName(option.groupId,arabic)} ${sectionName(option.sectionId,arabic)} ${arabic?option.effectAr:option.effectEn}`);
    const keywordOnly=isSearchResult&&normalizedQuery.split(" ").some(word=>!displayed.includes(word));
    return (
      <button
        className={`nec-option${option.disabled ? " is-disabled" : ""}${isSearchResult && option.id === firstEnabledId ? " is-first-result" : ""}`}
        type="button"
        key={`${option.id}-${index}`}
        disabled={option.disabled}
        aria-label={title}
        onClick={option.onSelect}
        dir={arabic ? "rtl" : "ltr"}
      >
        <span className="nec-option-copy">
          <span className="nec-option-title">{isSearchResult ? <HighlightedText text={title} query={query} /> : title}</span>
          {description && <span className="nec-option-description">{isSearchResult ? <HighlightedText text={description} query={query} /> : description}</span>}
          {keywordOnly&&<span className="nec-option-description">{arabic?"مطابقة":"Match"}: <HighlightedText text={query} query={query}/></span>}
          {option.effectEn || option.effectAr ? (
            <span className="nec-option-effect">{isSearchResult ? <HighlightedText text={arabic ? option.effectAr || option.effectEn || "" : option.effectEn || option.effectAr || ""} query={query} /> : (arabic ? option.effectAr || option.effectEn : option.effectEn || option.effectAr)}</span>
          ) : null}
        </span>
        <ArrowRight className="nec-row-arrow" size={16} aria-hidden="true" />
      </button>
    );
  };

  const renderGroup = (groupId: string, groupOptions: NewEntryOption[], isDelivery = false) => {
    const knownGroup = GROUP_BY_ID.get(groupId as NewEntryGroupId);
    const Icon = knownGroup?.icon || Package;
    const open = effectiveExpanded.includes(groupId as NewEntryGroupId);
    const deliveryUnavailable = isDelivery && (deliveryLoading || !!deliveryError);
    return (
      <div className="nec-group" key={groupId}>
        <button
          className="nec-group-toggle"
          type="button"
          aria-expanded={open}
          onClick={() => toggleGroup(groupId as NewEntryGroupId)}
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <Icon className="nec-group-icon" size={17} strokeWidth={1.8} aria-hidden="true" />
          <span className="nec-group-heading">
            <strong>{groupName(groupId, arabic)}</strong>
            {!open && !deliveryUnavailable && (
              <small>{groupOptions.slice(0, 3).map(option => arabic ? option.nameAr || option.nameEn : option.nameEn || option.nameAr).join(" · ")}</small>
            )}
          </span>
          <span className="nec-group-count">{groupOptions.length}</span>
        </button>
        {open && (
          <div className="nec-group-options">
            {deliveryUnavailable ? (
              deliveryLoading ? (
                <div className="nec-status" role="status"><span className="nec-skeleton" /><span>{arabic ? "جارٍ تحميل خيارات التوصيل…" : "Loading delivery options…"}</span></div>
              ) : (
                <div className="nec-status nec-status-error" role="alert">
                  <span>{arabic ? "تعذر تحميل شركات التوصيل." : "Could not load delivery companies."}</span>
                  {onRetryDelivery && <button type="button" className="nec-retry" onClick={onRetryDelivery}>{arabic ? "إعادة المحاولة" : "Retry"}</button>}
                </div>
              )
            ) : groupOptions.length ? groupOptions.map((option, index) => renderOption(option, index)) : (
              <div className="nec-status">{arabic ? "لا توجد خيارات متاحة." : "No options available."}</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSection = (sectionId: NewEntrySectionId) => {
    const sectionOptions = visibleOptions.filter(option => option.sectionId === sectionId && option.id !== "opening_balance");
    const groupIds: string[] = [];
    sectionOptions.forEach(option => { if (!groupIds.includes(option.groupId)) groupIds.push(option.groupId); });
    if (!searching && sectionId === "delivery" && !groupIds.includes("delivery")) groupIds.push("delivery");
    const section = SECTION_COPY[sectionId];
    const count = sectionId === "delivery" && (deliveryLoading || deliveryError) && !searching
      ? options.filter(option => option.sectionId === sectionId && option.id !== "opening_balance").length
      : sectionOptions.length;
    return (
      <section className={`nec-section nec-section-${sectionId}`} key={sectionId}>
        <div className={`nec-section-heading nec-accent-${section.color}`}>
          <span className="nec-section-label">{arabic ? section.ar : section.en}</span>
          <span className="nec-section-count">{count}</span>
        </div>
        <div className="nec-section-card">
          {groupIds.map(groupId => {
            const groupOptions = sectionOptions.filter(option => option.groupId === groupId);
            return renderGroup(groupId, groupOptions, sectionId === "delivery" && groupId === "delivery");
          })}
        </div>
      </section>
    );
  };

  const setAllGroups = (open: boolean) => {
    const ids = open ? GROUPS.map(group => group.id) : [];
    setExpanded({ identity, ids });
  };
  const expenseOption = options.find(option => option.id === "expense");
  const hasResults = visibleOptions.length > 0;
  const balanceOption = openingBalanceOption || options.find(option => option.id === "opening_balance");
  const deliveryMatches = visibleOptions.some(option => option.sectionId === "delivery");
  const deliverySearchCorpus = normalizeArabic("delivery company شركة التوصيل delivery_pay delivery_charge delivery_received talabat instashop rider طلبات خصم تحصيل");
  const deliveryQueryMatches = searching && normalizedQuery.split(" ").filter(Boolean).every(word => deliverySearchCorpus.includes(word));
  const showUnresolvedDeliveryStatus = !deliveryMatches && deliveryQueryMatches && (!!deliveryLoading || !!deliveryError);
  const firstDeliveryMatchId = visibleOptions.find(option => option.sectionId === "delivery")?.id;
  const placeholder = arabic ? "ابحث عن خيار، مثل توريد أو مرتب أو بنزين" : "Search options, e.g. توريد, salary, gas";

  return (
    <div className="branch-entry-chooser" dir={arabic ? "rtl" : "ltr"} lang={arabic ? "ar" : "en"}>
      <div className="nec-intro">
        <p className="nec-subtitle">
          {arabic ? "اختر ما حدث للنقد في الصندوق." : "Choose what happened to the cash in the box."}
          {branchName && <><span className="nec-subtitle-dot"> · </span><strong>{branchName}</strong></>}
        </p>
        <label className="nec-search">
          <Search size={17} aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            placeholder={placeholder}
            value={query}
            onChange={event => setQuery(event.target.value)}
            onKeyDown={handleInputKeyDown}
            aria-label={arabic ? "ابحث عن خيار" : "Search entry options"}
            autoComplete="off"
          />
          {!arabic && <kbd>/</kbd>}
        </label>
      </div>

      {searching ? (
        <div className="nec-search-results nec-section-card">
          {visibleOptions.map((option, index) => (
            <div className="nec-search-result" key={`${option.id}-${index}`}>
              <div className="nec-result-path" dir={arabic ? "rtl" : "ltr"}>
                <span><HighlightedText text={sectionName(option.sectionId, arabic)} query={query}/></span>
                <span className="nec-path-divider" aria-hidden="true">{arabic ? "‹" : "›"}</span>
                <span><HighlightedText text={groupName(option.groupId, arabic)} query={query}/></span>
              </div>
              {renderOption(option, index, true)}
              {option.sectionId === "delivery" && option.id === firstDeliveryMatchId && deliveryLoading && deliveryMatches && (
                <div className="nec-status" role="status"><span className="nec-skeleton" /><span>{arabic ? "جارٍ تحميل خيارات التوصيل…" : "Loading delivery options…"}</span></div>
              )}
              {option.sectionId === "delivery" && option.id === firstDeliveryMatchId && deliveryError && deliveryMatches && (
                <div className="nec-status nec-status-error" role="alert">
                  <span>{typeof deliveryError === "string" ? deliveryError : (arabic ? "تعذر تحميل شركات التوصيل." : "Could not load delivery companies.")}</span>
                  {onRetryDelivery && <button type="button" className="nec-retry" onClick={onRetryDelivery}>{arabic ? "إعادة المحاولة" : "Retry"}</button>}
                </div>
              )}
            </div>
          ))}
          {showUnresolvedDeliveryStatus && (
            <div className="nec-search-result">
              <div className="nec-result-path" dir={arabic ? "rtl" : "ltr"}>
                <span>{arabic ? SECTION_COPY.delivery.ar : SECTION_COPY.delivery.en}</span>
                <span className="nec-path-divider" aria-hidden="true">{arabic ? "‹" : "›"}</span>
                <span>{groupName("delivery", arabic)}</span>
              </div>
              <div className="nec-status" role={deliveryError ? "alert" : "status"}>
                {deliveryLoading && <span className="nec-skeleton" />}
                <span>{deliveryLoading ? (arabic ? "جارٍ تحميل خيارات التوصيل…" : "Loading delivery options…") : (typeof deliveryError === "string" ? deliveryError : (arabic ? "تعذر تحميل شركات التوصيل." : "Could not load delivery companies."))}</span>
                {deliveryError && onRetryDelivery && <button type="button" className="nec-retry" onClick={onRetryDelivery}>{arabic ? "إعادة المحاولة" : "Retry"}</button>}
              </div>
            </div>
          )}
          {!hasResults && !showUnresolvedDeliveryStatus && (
            <div className="nec-no-results" role="status">
              <strong>{arabic ? `لا يوجد خيار يطابق “${query}”` : `No option matches “${query}”.`}</strong>
              <span>{arabic ? "إذا لم تكن متأكدًا، سجّل مصروفًا وسيقوم قسم المالية بتصنيفه." : "If you're not sure, use Record an expense and finance will classify it."}</span>
              {expenseOption && <button type="button" className="nec-expense-fallback" disabled={expenseOption.disabled} onClick={expenseOption.onSelect}>{arabic ? "تسجيل مصروف" : "Record an expense"}<ArrowRight size={15} aria-hidden="true" /></button>}
            </div>
          )}
        </div>
      ) : (
        <>
          {renderSection("delivery")}
          <div className="nec-expand-controls">
            <span>{arabic ? "المجموعات" : "Groups"}</span>
            <button type="button" onClick={() => setAllGroups(true)}>{arabic ? "توسيع الكل" : "Expand all"}</button>
            <span className="nec-control-divider">·</span>
            <button type="button" onClick={() => setAllGroups(false)}>{arabic ? "طي الكل" : "Collapse all"}</button>
          </div>
          <div className="nec-columns">
            {renderSection("out")}
            {renderSection("in")}
          </div>
        </>
      )}

      {balanceOption && !searching && (
        <button
          className="nec-opening-balance"
          aria-label={arabic?"رصيد افتتاحي":"Opening balance"}
          type="button"
          disabled={balanceOption.disabled}
          onClick={balanceOption.onSelect}
        >
          <small>{arabic ? "للمالية فقط" : "Finance only"}</small>
          <span className="nec-opening-divider">·</span>
          <span>{arabic ? "رصيد افتتاحي" : "Opening balance"}</span>
          <ArrowRight size={15} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
